/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, Typography, Box } from '@material-ui/core';
import { Language, ExpandMore } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import useLanguageMenu from './useLanguageMenu';
import Button from '../../components/common/Button';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { KeyboardActions } from '../../shared/enums/KeyboardActions';

const useStyles = makeStyles(theme => ({
  menuIcon: {
    marginRight: theme.spacing(1),
    width: '30px',
    height: '30px',
  },
  spacer: {
    marginRight: theme.spacing(1),
  },
  languageText: {
    fontSize: '1.15rem',
    fontWeight: 600,
    marginTop: theme.spacing(0.25),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  languageNav: {
    display: 'flex',
    flex: '0 0 auto',
    alignItems: 'center',
  },
}));

export default function LanguageMenu({ className }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { anchorEl, handleClose, handleClick, changeLanguage } = useLanguageMenu();
  const frontendCustomisation = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const frontendConfig = frontendCustomisation.resource;
  const languages = frontendConfig?.languages?.availableLanguages;

  const handleKeyDown = (e) => {
    if (e.keyCode === KeyboardActions.ARROW_UP || e.keyCode === KeyboardActions.ARROW_DOWN) {
      handleClick(e);
    }
  };

  return (
    <Box className={clsx(className, classes.languageNav)} role="toolbar" aria-label={t('aria.header.language')}>
      <Button
        onKeyDown={handleKeyDown}
        variant="text"
        id="language"
        size="small"
        color="inherit"
        onClick={handleClick}
        aria-controls="simple-menu"
      >
        <Language fontSize="large" className={classes.menuIcon} />
        <Typography className={classes.languageText} variant="body2">
          {t(i18n.language).toUpperCase()}
        </Typography>
        <ExpandMore />
      </Button>

      <Menu
        id="simple-menu"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        style={{ zIndex: '3000' }}
      >
        {languages &&
          Object.entries(languages).map(([lng, lngKey]) => (
            <MenuItem key={lngKey} onClick={() => changeLanguage(lng)} data-value={lngKey}>
              <Button
                aria-label={t('language.change.label', { lng: `$t(${lngKey})` })}
                variant="text"
                size="small"
                color="inherit"
                className={classes.spacer}
              >
                {t(lngKey)}
              </Button>
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
}
